/** Sample the dominant color of an image (e.g. the slide background bitmap). */

function toLocalDrawableUrl(src: string): Promise<{ url: string; revoke?: () => void }> {
  if (src.startsWith("blob:") || src.startsWith("data:")) return Promise.resolve({ url: src });
  return fetch(src, { cache: "no-store" }).then(async (res) => {
    if (!res.ok) throw new Error(`Fetch background (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    return { url, revoke: () => URL.revokeObjectURL(url) };
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Caricamento immagine fallito"));
    img.src = src;
  });
}

function toHex(r: number, g: number, b: number): string {
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

/**
 * Most-frequent quantized color of the image, ignoring transparent/near-edge noise.
 * Downscales for speed; buckets to 24-level steps so anti-alias variants collapse.
 */
export async function sampleDominantColor(src: string): Promise<string | null> {
  const { url, revoke } = await toLocalDrawableUrl(src);
  try {
    const img = await loadImage(url);
    const w = 48;
    const h = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * w)) || 48;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const STEP = 24;
    const counts = new Map<string, { n: number; r: number; g: number; b: number }>();
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3]!;
      if (a < 200) continue;
      const r = data[i]!;
      const g = data[i + 1]!;
      const b = data[i + 2]!;
      const key = `${Math.round(r / STEP)}-${Math.round(g / STEP)}-${Math.round(b / STEP)}`;
      const e = counts.get(key);
      if (e) {
        e.n += 1;
        e.r += r;
        e.g += g;
        e.b += b;
      } else {
        counts.set(key, { n: 1, r, g, b });
      }
    }
    let best: { n: number; r: number; g: number; b: number } | null = null;
    for (const e of counts.values()) {
      if (!best || e.n > best.n) best = e;
    }
    if (!best) return null;
    return toHex(Math.round(best.r / best.n), Math.round(best.g / best.n), Math.round(best.b / best.n));
  } finally {
    revoke?.();
  }
}
